import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { startsWith } from 'lodash';
import cx from 'classnames';
import {
  Intent,
  Classes,
  Position,
  ResizeSensor,
  Icon,
  Menu,
  MenuItem,
  MenuDivider,
  Button,
  ButtonGroup,
  Tabs,
  Tab,
  Divider,
  Tag,
} from '@blueprintjs/core';
import { Popover2 } from '@blueprintjs/popover2';
import { PERMISSIONS } from '@moleculeone/auth';

import Protected from '@f/components/shared/protected/Protected';
import { signOut } from '@f/services/authentication/authentication.actions';
import { useConfig, useNavigationMenuElements } from '@f/hooks';

import { MenuElement } from './MenuElement';
import cls from './Nav.module.css';

const DEFAULT_CONTROL_BUTTON_GROUP_WIDTH = 825;

export default function () {
  const [controlMenuTreshhold, setControlMenuTreshhold] = useState(DEFAULT_CONTROL_BUTTON_GROUP_WIDTH);
  const [isControlMenuCompact, setControlMenuCompact] = useState(false);

  const user = useSelector((state: any) => state.authentication.user);
  const location = useSelector((state: any) => state.router.location);

  const config = useConfig();
  const dispatch = useDispatch();

  const menuElements = useNavigationMenuElements();

  const handleControlButtonGroupRef = (ref: HTMLDivElement | null) => {
    if (ref) {
      setControlMenuTreshhold(ref.offsetWidth + 1);
    }
  };

  const decodedPathname = useMemo(() => decodeURIComponent(location.pathname), [location.pathname]);

  const selectedTabId = useMemo(() => {
    const availableElements = Object.values(menuElements);

    const selectedElement = availableElements.find(element => startsWith(decodedPathname, element.path));

    if (!selectedElement) {
      return '';
    }

    return selectedElement.tabId;
  }, [location.pathname]);

  const onResizeControlMenu = (entries: ResizeObserverEntry[]) => {
    if (!controlMenuTreshhold) {
      return;
    }

    const [{ contentRect }] = entries;
    const shouldBeCompact = contentRect.width < controlMenuTreshhold;

    if (isControlMenuCompact !== shouldBeCompact) {
      setControlMenuCompact(shouldBeCompact);
    }
  };

  return (
    <>
      <div className={cls.featureMenu} data-test="feature-navigation">
        <Tabs large selectedTabId={selectedTabId}>
          {menuElements['synthesis-plans'] && (
            <Tab className={cls.featureTab} id={menuElements['synthesis-plans'].tabId}>
              <MenuElement element={menuElements['synthesis-plans']} />
            </Tab>
          )}

          {menuElements['starting-materials'] && (
            <Tab className={cls.featureTab} id={menuElements['starting-materials'].tabId}>
              <MenuElement element={menuElements['starting-materials']} />
            </Tab>
          )}

          {menuElements.reports && (
            <Tab className={cls.featureTab} id={menuElements.reports.tabId}>
              <MenuElement element={menuElements.reports} />
            </Tab>
          )}
        </Tabs>
      </div>

      <ResizeSensor onResize={onResizeControlMenu}>
        <div className={cls.controlMenu} data-test="control-navigation">
          {!isControlMenuCompact && (
            <div ref={handleControlButtonGroupRef}>
              <ButtonGroup minimal>
                <Popover2
                  placement={Position.BOTTOM}
                  content={
                    <Menu large>
                      <NavLink
                        className={cx(Classes.MENU_ITEM, Classes.POPOVER_DISMISS, cls.link, 'bp3-icon-properties')}
                        to="/dashboard/settings/synthesis-plan-parameters"
                        activeClassName={cx(Classes.ACTIVE, Classes.INTENT_PRIMARY)}
                        data-test="synthesis-plan-parameters-button"
                      >
                        Synthesis plan parameters
                      </NavLink>

                      <Protected allowedPermissions={[PERMISSIONS.RETRO.API.READ]}>
                        <NavLink
                          className={cx(Classes.MENU_ITEM, Classes.POPOVER_DISMISS, cls.link, 'bp3-icon-barcode')}
                          to="/dashboard/settings/api-tokens"
                          activeClassName={cx(Classes.ACTIVE, Classes.INTENT_PRIMARY)}
                          data-test="api-tokens-button"
                        >
                          API tokens
                        </NavLink>
                      </Protected>
                    </Menu>
                  }
                >
                  <Button
                    minimal
                    intent={startsWith(decodedPathname, '/dashboard/settings') ? Intent.PRIMARY : Intent.NONE}
                    active={startsWith(decodedPathname, '/dashboard/settings')}
                    text="Settings"
                    icon="settings"
                    rightIcon="chevron-down"
                    data-test="user-settings-button"
                  />
                </Popover2>

                <Protected allowedPermissions={[PERMISSIONS.RETRO.APP.ADMIN]}>
                  <NavLink
                    className={cx(Classes.BUTTON, cls.link)}
                    to="/dashboard/administration"
                    activeClassName={cx(Classes.ACTIVE, Classes.INTENT_PRIMARY)}
                    data-test="administration-button"
                  >
                    <Icon icon="dashboard" />
                    <span className={Classes.BUTTON_TEXT}>Administration</span>
                  </NavLink>
                </Protected>

                <a className={cx(Classes.BUTTON, cls.link)} href={config.hubUrl} target="_blank" rel="noreferrer">
                  <Icon icon="people" />
                  <span className={Classes.BUTTON_TEXT}>Hub</span>
                </a>

                <Divider />
                {/* If the user has an individual account */}
                {user.email !== user.organizationName && (
                  <Tag round minimal intent={Intent.PRIMARY} className={cls.userOrganization}>
                    {user.organizationName}
                  </Tag>
                )}
                <span className={cls.userEmail}>{user.email}</span>

                <Button text="Sign out" icon="power" onClick={() => dispatch(signOut)} data-test="sign-out-button" />
              </ButtonGroup>
            </div>
          )}

          {isControlMenuCompact && (
            <Popover2
              placement={Position.BOTTOM}
              content={
                <Menu large>
                  <NavLink
                    className={cx(Classes.MENU_ITEM, Classes.POPOVER_DISMISS, cls.link, 'bp3-icon-settings')}
                    to="/dashboard/settings"
                    activeClassName={cx(Classes.ACTIVE, Classes.INTENT_PRIMARY)}
                    data-test="settings-button"
                  >
                    Settings
                  </NavLink>

                  <Protected allowedPermissions={[PERMISSIONS.RETRO.APP.ADMIN]}>
                    <NavLink
                      className={cx(Classes.MENU_ITEM, Classes.POPOVER_DISMISS, cls.link, 'bp3-icon-dashboard')}
                      to="/dashboard/administration"
                      activeClassName={cx(Classes.ACTIVE, Classes.INTENT_PRIMARY)}
                      data-test="administration-button"
                    >
                      Administration
                    </NavLink>
                  </Protected>

                  <a
                    className={cx(Classes.MENU_ITEM, Classes.POPOVER_DISMISS, cls.link, 'bp3-icon-people')}
                    href={config.hubUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Hub
                  </a>

                  <MenuDivider />

                  <MenuItem
                    text="Sign out"
                    icon="power"
                    onClick={() => dispatch(signOut)}
                    data-test="sign-out-button"
                  />
                </Menu>
              }
            >
              <Button minimal rightIcon="chevron-down" data-test="user-settings-button">
                {/* If the user has an individual account */}
                {user.email !== user.organizationName && (
                  <Tag round minimal intent={Intent.PRIMARY}>
                    {user.organizationName}
                  </Tag>
                )}{' '}
                {user.email}
              </Button>
            </Popover2>
          )}
        </div>
      </ResizeSensor>
    </>
  );
}
